/* eslint-disable no-unused-vars */
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLanguage } from "../../context/LanguageContext";
import MenuAIIcon from "../../assets/images/icon/menuaiIcon.png";
import PauseIcon from "../../assets/images/icon/pause.png";
import ChatService from "../../services/chat";
import TrainingService from "../../services/training-group";
import { getGlobalSocket, initGlobalSocket } from "../../services/socket";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { generatePdfFromHtml } from "../../utils/pdfFonts";
import { generateWordFromHtml } from "../../utils/wordExport";
import { markdownToHtml, MD_PDF_STYLE } from "../../utils/markdownHtml";
import { toastSuccess, toastError } from "../Toast";
import { readStoredMasterPrompt } from "../../hooks/useSidebarData";

const getTimestamp = () => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(now.getHours())}-${pad(now.getMinutes())}`;
};

const getDateForFile = () => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

const normalizeItems = (items) => {
  if (!Array.isArray(items)) return [];
  return items
    .map((item, idx) => {
      if (typeof item === "string") {
        return { title: item, description: "", text: item, _id: `item_${idx}` };
      }
      return {
        title: item?.title || item?.text || "",
        description: item?.description || "",
        text: item?.text || item?.title || "",
        _id: item?._id || `${item?.title || "item"}_${idx}`,
      };
    })
    .filter((item) => (item.title && item.title.trim()) || (item.text && item.text.trim()));
};

const MenuAIPanel = ({ isOpen, onClose, presets = [], originalText = "", translatedText = "", onResult, mode = "chat", translationType = "text", hasChatHistory }) => {
  const { t } = useLanguage();
  const reduxUser = useSelector((state) => state.user?.user);
  const panelRef = useRef(null);
  const translationTypeRef = useRef(translationType);
  translationTypeRef.current = translationType;

  const [workModes, setWorkModes] = useState([]);
  const [aiFramework, setAiFramework] = useState([]);
  const [frameworkStatus, setFrameworkStatus] = useState(true);
  const [selectedPresetId, setSelectedPresetId] = useState("");
  const [selectedPreset, setSelectedPreset] = useState("");
  const [prompt, setPrompt] = useState("");
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resultText, setResultText] = useState("");
  const [resultTranslation, setResultTranslation] = useState("");
  const [showResult, setShowResult] = useState(false);
  const [pendingResult, setPendingResult] = useState(null);
  const [showDownloadPicker, setShowDownloadPicker] = useState(false);
  const pendingDownloadRef = useRef("");
  const pendingDownloadObjRef = useRef(null);

  const isChatMode = mode === "chat";
  const isTranslateMode = mode === "translate";
  const isTranscribeMode = mode === "transcribe";

  const hasChatData =
    hasChatHistory !== undefined
      ? Boolean(hasChatHistory)
      : Boolean(originalText?.trim() || translatedText?.trim());
  const hasTextData = Boolean(originalText?.trim() || translatedText?.trim());

  let isDataMissing = false;
  let emptyTooltipMessage = "";

  if (isChatMode) {
    isDataMissing = !hasChatData;
    emptyTooltipMessage = t.noChatHistoryFound || "No chat history found. Please start a conversation first.";
  } else if (isTranslateMode) {
    isDataMissing = !hasTextData;
    emptyTooltipMessage = t.noTranslateDataFound || "No text found to translate. Please enter text first.";
  } else if (isTranscribeMode) {
    isDataMissing = !hasTextData;
    emptyTooltipMessage = t.noTranscribeDataFound || "No transcription found. Please transcribe audio first.";
  }

  const isGenerateDisabled = isSubmitting || isDataMissing;

  useEffect(() => {
    if (presets && presets.length > 0) {
      const normalized = normalizeItems(presets);
      if (normalized.length > 0) {
        setWorkModes(normalized);
      }
    }
  }, [presets]);

  useEffect(() => {
    if (!isOpen) return;
    const key = mode === "chat" ? "chat" : mode === "transcribe" ? "transcribe" : "translate";

    // Load from Redux user if available
    const reduxGroup = reduxUser?.trainingGroupId;
    const reduxItems = reduxGroup?.[key];
    if (Array.isArray(reduxItems) && reduxItems.length > 0) {
      setWorkModes(normalizeItems(reduxItems));
    }
    if (reduxGroup) {
      if (reduxGroup.status !== undefined) {
        setFrameworkStatus(Boolean(reduxGroup.status));
      }
      if (Array.isArray(reduxGroup.aiFramework)) {
        setAiFramework(normalizeItems(reduxGroup.aiFramework));
      }
    }

    TrainingService.getAllSuggettions()
      .then((res) => {
        const d = res?.data?.data || {};
        const list = d[key] || d[mode] || [];
        const normalized = normalizeItems(list);
        if (normalized.length > 0) {
          setWorkModes(normalized);
        } else if (!reduxItems || reduxItems.length === 0) {
          setWorkModes([]);
        }

        if (d.status !== undefined) {
          setFrameworkStatus(Boolean(d.status));
        }
        if (Array.isArray(d.aiFramework)) {
          setAiFramework(normalizeItems(d.aiFramework));
        }
      })
      .catch(() => { });
  }, [isOpen, mode, reduxUser]);

  useEffect(() => {
    if (!isOpen) return;
    const stored = readStoredMasterPrompt();
    if (stored) setPrompt(stored);

    const handleProfileUpdate = () => {
      const updated = readStoredMasterPrompt();
      if (updated) setPrompt(updated);
    };
    window.addEventListener("profileUpdated", handleProfileUpdate);
    return () => window.removeEventListener("profileUpdated", handleProfileUpdate);
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) onClose();
    };
    if (isOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onClose]);

  const validate = () => {
    return true;
  };

  const callChatApi = async () => {
    const body = {
      presetPrompt: selectedPreset || "",
    };
    const sock = getGlobalSocket() || initGlobalSocket();

    return new Promise(async (resolve) => {
      let isResolved = false;

      const finish = (result) => {
        if (isResolved) return;
        isResolved = true;
        cleanup();
        resolve(result);
      };

      const extractPayload = (raw) => {
        if (!raw) return null;
        let d = raw;
        if (typeof d === "string") {
          try {
            d = JSON.parse(d);
          } catch (_) {
            return { message: d, status: "error" };
          }
        }
        if (Array.isArray(d) && d.length > 0) {
          d = d[0];
        }
        if (d?.data && typeof d.data === "object" && !Array.isArray(d.data)) {
          return { ...d.data, ...d };
        }
        return d;
      };

      const handleSocketResponse = (...args) => {
        const rawEvent = args[0] !== undefined ? args[0] : args[1];
        if (!rawEvent) return;
        const payload = extractPayload(rawEvent);
        if (!payload) return;

        const status = payload?.status || payload?.data?.status;
        const statusCode = payload?.statusCode ?? payload?.data?.statusCode;

        const isError =
          status === "error" ||
          status === "failed" ||
          status === false ||
          (typeof statusCode === "number" && statusCode >= 400) ||
          Boolean(payload?.error) ||
          Boolean(payload?.data?.error) ||
          Boolean(payload?.isError);

        if (isError) {
          const errMsg =
            payload?.message ||
            payload?.data?.message ||
            payload?.error ||
            payload?.data?.error ||
            (typeof payload === "string" ? payload : "") ||
            t?.somethingWentWrong ||
            "Something went wrong";
          if (!isResolved) {
            isResolved = true;
            cleanup();
            toastError(errMsg);
            resolve(null);
          }
          return;
        }

        const isDone =
          status === "completed" ||
          status === "done" ||
          status === "success" ||
          ((statusCode === 200 || !statusCode) && status !== "processing" && status !== "continue" && Boolean(payload?.data || payload?.translation || payload?.response || payload?.answer || payload?.text || payload?.message));

        if (isDone) {
          const rawPi =
            payload?.personalIdentifiersProtected ??
            payload?.data?.personalIdentifiersProtected ??
            payload?.personal_identifiers_protected ??
            payload?.data?.personal_identifiers_protected ??
            null;
          const pi = rawPi !== null && rawPi !== undefined && !isNaN(Number(rawPi)) ? Number(rawPi) : null;

          const content =
            payload?.data ??
            payload?.translation ??
            payload?.translatedText ??
            payload?.transcription ??
            payload?.response ??
            payload?.answer ??
            payload?.text ??
            payload?.message ??
            null;

          if (content !== null && content !== undefined) {
            if (Array.isArray(content)) {
              finish(content);
            } else if (typeof content === "object") {
              finish({
                ...content,
                personalIdentifiersProtected: pi !== null ? pi : (content?.personalIdentifiersProtected ?? content?.personal_identifiers_protected),
              });
            } else {
              finish({
                translation: String(content),
                response: String(content),
                personalIdentifiersProtected: pi,
              });
            }
          }
        }
      };

      const relevantEvents = [
        "chat-preset-response",
        "chat-translation-preset-response",
        "chat-audio-preset-response",
        "chat-translation-response",
        "chat-audio-response",
        "translation-result",
        "chat-response",
      ];

      const handleAny = (evt, ...args) => {
        const ev = String(evt).toLowerCase();
        if (ev.includes("preset") || ev.includes("chat-response") || ev.includes("translation-result")) {
          handleSocketResponse(args[0]);
        }
      };

      if (sock) {
        relevantEvents.forEach((evt) => {
          sock.on(evt, handleSocketResponse);
        });
        if (sock.onAny) {
          sock.onAny(handleAny);
        }
      }

      const cleanup = () => {
        if (sock) {
          relevantEvents.forEach((evt) => {
            sock.off(evt, handleSocketResponse);
          });
          if (sock.offAny) {
            sock.offAny(handleAny);
          }
        }
      };

      const runHttpFallback = async () => {
        if (isResolved) return;
        try {
          let res;
          if (mode === "transcribe") {
            res = await ChatService.aiChatAudioPreset(body);
            const toSegment = (r) => ({
              timestamp: r?.timestamp || "",
              person: r?.person || r?.speaker || "",
              originalText: r?.transcription || r?.originalText || "",
              translatedText: r?.translation || r?.translatedText || "",
            });
            const collectSegments = (r) => {
              const out = [];
              if (Array.isArray(r?.segments) && r.segments.length > 0) {
                r.segments.forEach((s) => out.push(toSegment(s)));
              } else {
                const s = toSegment(r);
                if (s.originalText || s.translatedText) out.push(s);
              }
              return out;
            };
            const root = res?.data?.data ?? res?.data;
            let segs = [];
            if (Array.isArray(root)) {
              root.forEach((r) => segs.push(...collectSegments(r)));
            } else {
              const d = root && typeof root === "object" ? root : null;
              if (d && (d.translation || d.translatedText || d.transcription)) segs = collectSegments(d);
            }
            if (segs.length > 0) {
              finish({
                transcription: segs.map((s) => s.originalText).filter(Boolean).join(" "),
                translation: segs.map((s) => s.translatedText).filter(Boolean).join(" "),
                segments: segs,
              });
              return;
            }
          } else if (mode === "translate") {
            const type = translationTypeRef.current || "text";
            res = await ChatService.aiChatTranslationPreset({ ...body, type });
            const d = res?.data?.data;
            if (d && (d.originalText || d.extractedText || d.translation)) {
              finish({ originalText: d.originalText || d.extractedText || "", translation: d.translation || "" });
              return;
            }
          } else {
            res = await ChatService.aiChat(body);
          }

          if (res?.data?.status === "error" || (res?.data?.statusCode && res?.data?.statusCode >= 400)) {
            const errMsg =
              res?.data?.message ||
              res?.data?.error ||
              t?.somethingWentWrong ||
              "Something went wrong";
            if (!isResolved) {
              isResolved = true;
              cleanup();
              toastError(errMsg);
              resolve(null);
            }
            return;
          }

          const raw =
            res?.data?.response ||
            res?.data?.answer ||
            res?.data?.text ||
            res?.data?.message ||
            res?.data?.data;
          if (raw) {
            if (Array.isArray(raw)) finish(raw);
            else if (typeof raw === "string") finish(raw);
            else {
              const extracted =
                raw.translation ||
                raw.transcription ||
                raw.response ||
                raw.answer ||
                raw.text ||
                raw.message ||
                null;
              if (extracted) finish(extracted);
              else finish(raw);
            }
          }
        } catch (_err) {
          if (!isResolved) {
            isResolved = true;
            cleanup();
            const errMsg =
              _err?.response?.data?.message ||
              _err?.response?.data?.error ||
              _err?.message;
            if (errMsg) {
              toastError(errMsg);
            }
            resolve(null);
          }
        }
      };

      // Primary path: Use Socket when connected
      let socketEmitted = false;
      if (sock && sock.connected) {
        try {
          if (mode === "transcribe") {
            // console.log("[Global Socket] Emitting 'chat-audio-preset':", body);
            sock.emit("chat-audio-preset", body, (ack) => {
              if (ack) handleSocketResponse(ack);
            });
            socketEmitted = true;
          } else if (mode === "translate") {
            const type = translationTypeRef.current || "text";
            const translatePayload = { ...body, type };
            // console.log("[Global Socket] Emitting 'chat-translation-preset':", translatePayload);
            sock.emit("chat-translation-preset", translatePayload, (ack) => {
              if (ack) handleSocketResponse(ack);
            });
            socketEmitted = true;
          } else {
            // console.log("[Global Socket] Emitting 'chat-preset':", body);
            sock.emit("chat-preset", body, (ack) => {
              if (ack) handleSocketResponse(ack);
            });
            socketEmitted = true;
          }
        } catch (err) {
          console.warn("[Global Socket] Error emitting preset:", err);
          socketEmitted = false;
        }
      }

      // If socket is not connected or failed to emit, immediately call HTTP fallback
      if (!socketEmitted) {
        runHttpFallback();
      } else {
        // If socket emitted but no response received within 8 seconds, fallback to HTTP
        setTimeout(() => {
          if (!isResolved) {
            console.warn("[Global Socket] Socket preset timeout, falling back to HTTP...");
            runHttpFallback();
          }
        }, 8000);
      }

      // Absolute safety timeout
      setTimeout(() => {
        if (!isResolved) {
          cleanup();
          resolve(null);
        }
      }, 30000);
    });
  };

  const handlePlayClick = async () => {
    if (isDataMissing) {
      if (emptyTooltipMessage) toastError(emptyTooltipMessage);
      return;
    }
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      const improved = await callChatApi();
      if (improved) {
        if (onResult) {
          try {
            onResult(improved);
          } catch (_) { }
        }
        toastSuccess(t.generatedExecutiveSummarySuccess || "Generated Executive summary successfully");
        if (onClose) {
          onClose();
        }
      }
    } catch (_e) {
      const errMsg = _e?.response?.data?.message || _e?.response?.data?.error || _e?.message || t?.somethingWentWrong || "Something went wrong";
      toastError(errMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGenerateAndDownload = async () => {
    if (isDataMissing) {
      if (emptyTooltipMessage) toastError(emptyTooltipMessage);
      return;
    }
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      const improved = await callChatApi();
      if (improved) {
        if (onResult) {
          try {
            onResult(improved);
          } catch (_) { }
        }
        toastSuccess(t.generatedExecutiveSummarySuccess || "Generated Executive summary successfully");
        // Apply directly to UI and close the AI Framework sidebar without showing download popup
        if (onClose) {
          onClose();
        }
      }
    } catch (_e) {
      const errMsg = _e?.response?.data?.message || _e?.response?.data?.error || _e?.message || t?.somethingWentWrong || "Something went wrong";
      toastError(errMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const composeResultText = () => {
    if (pendingResult && Array.isArray(pendingResult) && pendingResult.length > 0) {
      return pendingResult.map((item) =>
        [item.question ? `Q: ${item.question}` : "", item.answer ? `A: ${item.answer}` : ""].filter(Boolean).join("\n")
      ).join("\n\n");
    } else if (pendingResult && Array.isArray(pendingResult.segments) && pendingResult.segments.length > 0) {
      return pendingResult.segments.map((s) =>
        [s.person, s.timestamp, s.originalText, s.translatedText].filter(Boolean).join("\n")
      ).join("\n\n");
    } else if (resultTranslation) {
      return (resultText || "") + (resultTranslation ? "\n\n" + resultTranslation : "");
    }
    return resultText;
  };

  const handleDownloadResult = () => {
    const text = composeResultText();
    if (!text) return;
    pendingDownloadRef.current = text;
    pendingDownloadObjRef.current =
      pendingResult && Array.isArray(pendingResult.segments) && pendingResult.segments.length > 0
        ? pendingResult
        : null;
    setShowDownloadPicker(true);
  };

  const doDownload = (format) => {
    setShowDownloadPicker(false);
    const text = pendingDownloadRef.current;
    if (!text) return;

    const data = pendingDownloadObjRef.current;
    const hasSegments = data && Array.isArray(data.segments) && data.segments.length > 0;

    const formatTimestamp = (ts) =>
      (ts || "").replace(/(\d+)-(\d+)s/g, (_, a, b) => {
        const toMMSS = (n) => `${String(Math.floor(Number(n) / 60)).padStart(2, "0")}:${String(Number(n) % 60).padStart(2, "0")}`;
        return `${toMMSS(a)} – ${toMMSS(b)}`;
      });
    const escapeHtml = (s) => (s || "").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const fileStamp = `${getDateForFile()} ${getTimestamp()}`;

    if (format === "text") {
      let content = "";
      if (hasSegments) {
        data.segments.forEach((s, i) => {
          const person = s.person || `Person ${i + 1}`;
          const ts = formatTimestamp(s.timestamp);
          content += `${person}  ${ts}\n`;
          if (s.originalText) content += `${s.originalText}\n`;
          content += `${s.translatedText}\n\n`;
        });
        content = content.trim();
      } else {
        content = text;
      }
      const cleanContent = (content || "").replace(/<\/?mark[^>]*>/gi, "").replace(/<[^>]+>/g, "");
      const blob = new Blob([cleanContent], { type: "text/plain;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `AI_Result_${fileStamp}.txt`;
      a.click();
      URL.revokeObjectURL(url);
    } else if (format === "word") {
      let contentHtml = "";
      if (hasSegments) {
        data.segments.forEach((s, i) => {
          const person = escapeHtml(s.person || `Person ${i + 1}`);
          const ts = escapeHtml(formatTimestamp(s.timestamp));
          contentHtml += `<div style="margin-bottom:14pt;">`;
          contentHtml += `<div style="font-weight:bold;font-size:12pt;color:#3D2B90;margin-bottom:4pt;">${person}  |  ${ts}</div>`;
          if (s.originalText) {
            contentHtml += `<div style="font-size:10pt;color:#716E89;font-style:italic;margin-left:8pt;margin-bottom:4pt;white-space:pre-wrap;">${escapeHtml(s.originalText)}</div>`;
          }
          if (s.translatedText) {
            contentHtml += `<div style="font-size:11pt;font-weight:bold;color:#28283c;white-space:pre-wrap;">${escapeHtml(s.translatedText)}</div>`;
          }
          contentHtml += `</div>`;
        });
      } else {
        contentHtml = `<div style="margin-bottom:14pt;">
          <div style="font-weight:bold;font-size:13pt;color:#3D2B90;margin-bottom:6pt;border-bottom:1px solid #E2DDF4;padding-bottom:3pt;">AI Result</div>
          <div style="font-size:11pt;line-height:1.6;">${markdownToHtml(text)}</div>
        </div>`;
      }
      generateWordFromHtml(contentHtml, `AI_Result_${fileStamp}.docx`);
    } else {
      let contentHtml = "";
      if (hasSegments) {
        data.segments.forEach((s, i) => {
          const person = escapeHtml(s.person || `Person ${i + 1}`);
          const ts = escapeHtml(formatTimestamp(s.timestamp));
          contentHtml += `<div style="margin-bottom:14px;" data-pdf-keep-together="1">`;
          contentHtml += `<div style="font-weight:700;font-size:11px;color:rgb(61,43,144);margin-bottom:2px;">${person}  |  ${ts}</div>`;
          if (s.originalText) {
            contentHtml += `<div style="font-size:9px;color:rgb(145,144,164);font-style:italic;margin-left:8px;white-space:pre-wrap;">${escapeHtml(s.originalText)}</div>`;
          }
          if (s.translatedText) {
            contentHtml += `<div style="font-size:10px;font-weight:700;color:rgb(40,40,60);white-space:pre-wrap;">${escapeHtml(s.translatedText)}</div>`;
          }
          contentHtml += `</div>`;
        });
      } else {
        contentHtml = `<div style="margin-bottom:12px;">
          <div style="font-weight:700;font-size:12px;color:rgb(61,43,144);margin-bottom:4px;">AI Result</div>
          <div style="font-size:11px;line-height:1.5;">${MD_PDF_STYLE}${markdownToHtml(text)}</div>
        </div>`;
      }
      generatePdfFromHtml(contentHtml, `AI_Result_${fileStamp}.pdf`);
    }
  };

  return (
    <>
      {isOpen && <div className="fixed inset-0 z-[998] sm:hidden bg-black/40" onClick={onClose} />}

      <div
        ref={panelRef}
        className={`fixed top-0 right-0 z-[999] h-full w-full sm:w-[360px] flex flex-col bg-tab-border transition-transform duration-300 ${isOpen ? "translate-x-0 shadow-[-4px_0_24px_rgba(61,43,144,0.15)]" : "translate-x-full"}`}
      >
        <div className="flex flex-col h-full px-5 pt-7 pb-4 gap-3.5 overflow-y-auto no-scrollbar">

          <button
            type="button"
            onClick={onClose}
            className="absolute top-4 right-4 w-7 h-7 flex items-center justify-center text-deep-purple hover:opacity-70 transition-opacity z-10"
            aria-label="Close panel"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 1L15 15M15 1L1 15" stroke="#3D2B90" strokeWidth="2.2" strokeLinecap="round" />
            </svg>
          </button>

          <div className="flex-shrink-0 flex items-center justify-center">
            <img src={MenuAIIcon} alt="AI" className="w-12 h-12 object-contain" />
          </div>

          <p className="flex-shrink-0 font-roboto font-bold text-[18px] uppercase leading-none tracking-[0.05em] text-deep-purple text-center mt-[4px]">
            {t.aiRules}
          </p>

          <p className="flex-shrink-0 font-roboto font-medium text-[11px] leading-[1.35] text-deep-purple/80 text-center max-w-[260px] mx-auto">
            {t.aiRulesSubtitle}
          </p>

          {/* AI FRAMEWORK Section (Rendered when status is true) */}
          {frameworkStatus && aiFramework.length > 0 && (
            <div className="flex-shrink-0 flex flex-col w-full mt-0.5">
              <div className="w-full p-3.5 rounded-[16px] bg-white border border-white/80 shadow-sm max-h-[180px] overflow-y-auto no-scrollbar flex flex-col gap-3">
                {aiFramework.map((item, idx) => (
                  <div
                    key={item._id || idx}
                    className={`w-full text-left flex items-start gap-2.5 ${idx > 0 ? "pt-2.5 border-t border-deep-purple/10" : ""
                      }`}
                  >
                    <span className="w-2 h-2 rounded-full bg-deep-purple flex-shrink-0 mt-1.5" />
                    <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                      {item.title && (
                        <p className="font-roboto font-bold text-[13px] text-deep-purple leading-snug break-words">
                          {item.title}
                        </p>
                      )}
                      {item.description && (
                        <p className="text-[11px] text-[#635E88] leading-tight break-words">
                          {item.description}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* WORK MODES Section (Positioned below AI Framework) */}
          <div className="flex-shrink-0 flex flex-col w-full gap-2 mt-0.5">
            <div className="flex flex-col w-full px-0.5">
              <div className="flex items-center gap-1.5">
                <span className="font-roboto font-bold text-[13px] uppercase text-deep-purple tracking-wide">
                  {t.presets}
                </span>
                <div className="relative group cursor-pointer text-deep-purple/70 hover:text-deep-purple" title={t.workModesDesc}>
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="16" x2="12" y2="12"></line>
                    <line x1="12" y1="8" x2="12.01" y2="8"></line>
                  </svg>
                </div>
              </div>
              <p className="text-[11px] font-normal text-deep-purple/80 mt-0.5 leading-snug">
                {t.workModesDesc}
              </p>
            </div>

            {/* Selectable Cards */}
            <div className="flex flex-col w-full gap-2 max-h-[380px] overflow-y-auto no-scrollbar py-0.5">
              {workModes.length > 0 ? (
                <>
                  {/* Default "No Work Mode" option */}
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedPresetId("");
                      setSelectedPreset("");
                    }}
                    className={`w-full text-left p-3 rounded-[14px] flex items-start gap-3 transition-all duration-200 border ${!selectedPresetId
                        ? "bg-[#ECE9F8] border-deep-purple/30 shadow-sm"
                        : "bg-white/90 hover:bg-white border-white/60 hover:border-deep-purple/20"
                      }`}
                  >
                    <div className="flex-shrink-0 mt-0.5">
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all ${!selectedPresetId ? "border-deep-purple bg-transparent" : "border-[#B4AED8] bg-white"
                        }`}>
                        {!selectedPresetId && <div className="w-2 h-2 rounded-full bg-deep-purple" />}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-roboto font-bold text-[13px] text-deep-purple leading-snug truncate">
                        {t.noWorkModeTitle}
                      </p>
                      <p className="text-[11px] text-[#635E88] mt-0.5 leading-tight">
                        {t.noWorkModeDesc}
                      </p>
                    </div>
                  </button>

                  {/* Mode items */}
                  {workModes.map((item, idx) => {
                    const itemId = item._id || `${item.title || "mode"}_${idx}`;
                    const isSelected = selectedPresetId === itemId;
                    return (
                      <button
                        key={itemId}
                        type="button"
                        onClick={() => {
                          setSelectedPresetId(itemId);
                          setSelectedPreset(item.text);
                        }}
                        className={`w-full text-left p-3 rounded-[14px] flex items-start gap-3 transition-all duration-200 border ${isSelected
                            ? "bg-[#ECE9F8] border-deep-purple/30 shadow-sm"
                            : "bg-white/90 hover:bg-white border-white/60 hover:border-deep-purple/20"
                          }`}
                      >
                        <div className="flex-shrink-0 mt-0.5">
                          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all ${isSelected ? "border-deep-purple bg-transparent" : "border-[#B4AED8] bg-white"
                            }`}>
                            {isSelected && <div className="w-2 h-2 rounded-full bg-deep-purple" />}
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-roboto font-bold text-[13px] text-deep-purple leading-snug truncate">
                            {item.title || item.text}
                          </p>
                          {item.description && (
                            <p className="text-[11px] text-[#635E88] mt-0.5 leading-tight line-clamp-2">
                              {item.description}
                            </p>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </>
              ) : (
                /* 3 Placeholder cards when no work modes assigned */
                [1, 2, 3].map((_, i) => (
                  <div
                    key={i}
                    className="w-full text-left p-3 rounded-[14px] flex items-start gap-3 bg-white/70 border border-white/60 opacity-80 cursor-default"
                  >
                    <div className="flex-shrink-0 mt-0.5">
                      <div className="w-4 h-4 rounded-full border-2 border-[#C0BADF] bg-white/50" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-roboto font-bold text-[13px] text-deep-purple/70 leading-snug">
                        {t.noWorkModeAssignedTitle}
                      </p>
                      <p className="text-[11px] text-[#7E78A4] mt-0.5 leading-tight">
                        {t.noWorkModeAssignedDesc}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* PROMPT container (hidden from UI, masterPrompt is still sent in socket/API payload) */}
          {/* <div
            className="flex flex-col w-full rounded-[14px] bg-white px-3.5 pt-3 pb-2.5 min-h-[140px] max-h-[180px] flex-shrink-0"
          >
            <span className="flex-shrink-0 font-roboto font-bold text-[13px] text-deep-purple uppercase text-center block mb-1.5">
              {t.promptLabel}
            </span>
            <textarea
              className="flex-1 min-h-0 bg-transparent resize-none focus:outline-none text-sm text-deep-purple placeholder-deep-purple/50 overflow-y-auto cursor-default select-text"
              placeholder={t.promptPlaceholder}
              value={prompt}
              readOnly
            />
            <div className="flex-shrink-0 flex justify-end mt-1">
              <button
                type="button"
                onClick={handlePlayClick}
                disabled={isSubmitting}
                className={`w-[31px] h-[31px] flex-shrink-0 flex items-center justify-center ${isSubmitting ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
              >
                {isSubmitting ? (
                  <span className="w-6 h-6 border-2 border-deep-purple/30 border-t-deep-purple rounded-full animate-spin" />
                ) : (
                  <img src={PauseIcon} alt="Play" className="w-full h-full object-contain" />
                )}
              </button>
            </div>
          </div> */}

          {/* Generate & Download */}
          <div
            className={`flex-shrink-0 mt-2 w-full ${isDataMissing ? "cursor-not-allowed" : ""}`}
            title={isDataMissing ? emptyTooltipMessage : undefined}
          >
            <button
              type="button"
              onClick={handleGenerateAndDownload}
              disabled={isGenerateDisabled}
              className={`flex items-center justify-center gap-2 w-full h-11 rounded-[67.52px] bg-deep-purple text-white font-roboto font-bold text-[13px] uppercase leading-none tracking-[0.05em] px-5 shadow-md shadow-deep-purple/20 ${
                isGenerateDisabled
                  ? "opacity-50 cursor-not-allowed pointer-events-none"
                  : "hover:bg-[#2e1f72] transition-colors"
              }`}
            >
              {isSubmitting ? (
                <>
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin flex-shrink-0" />
                  <span>{t.generateAndDownload}</span>
                </>
              ) : (
                t.generateAndDownload
              )}
            </button>
          </div>

          <p className="flex-shrink-0 font-roboto font-bold text-[11px] leading-[1.4] text-deep-purple text-center max-w-[260px] mx-auto">
            {t.applyFocusedEdits}
          </p>

        </div>
      </div>

      {lightboxOpen && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/70"
          onClick={() => setLightboxOpen(false)}
        >
          <img
            src={MenuAIIcon}
            alt="AI"
            className="max-w-[80vw] max-h-[80vh] object-contain rounded-2xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {showResult && (
        <div className="fixed inset-0 z-[1001] flex items-center justify-center bg-black/50 px-4 py-4">
          <div className="bg-white rounded-[20px] p-5 w-full max-w-lg flex flex-col gap-3 shadow-2xl max-h-[65vh] overflow-hidden">
            <h2 className="font-roboto font-black text-[14px] text-[#42445A] uppercase text-center pb-2 border-b border-[#E2DCF0]">
              {t.aiResult}
            </h2>
            <div className="flex-1 min-h-0 overflow-hidden flex flex-col gap-3">
              {pendingResult && Array.isArray(pendingResult.segments) && pendingResult.segments.length > 0 ? (
                <div className="flex-1 min-h-0 overflow-y-auto scrobar-custom flex flex-col gap-3" style={{ scrollbarWidth: 'thin', scrollbarColor: '#3D2B90 #C7BFE4' }}>
                  {pendingResult.segments.map((seg, i) => (
                    <div key={i} className="bg-[#F3F1F9] rounded-[12px] p-3 flex flex-col gap-2 flex-shrink-0">
                      {(seg.person || seg.timestamp) && (
                        <div className="flex items-center gap-2 flex-wrap">
                          {seg.person && (
                            <span className="font-roboto font-bold text-[11px] text-[#3D2B90] uppercase">{seg.person}</span>
                          )}
                          {seg.timestamp && (
                            <span className="font-roboto font-semibold text-[10px] text-[#9190A4] tabular-nums">[{seg.timestamp}]</span>
                          )}
                        </div>
                      )}
                      {seg.originalText && (
                        <div className="flex flex-col gap-1">
                          <p className="font-roboto font-bold text-[10px] text-[#3D2B90] uppercase">{t.original}</p>
                          <div className="text-[13px] leading-relaxed text-[#3D2B90] font-roboto ai-markdown">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{seg.originalText}</ReactMarkdown>
                          </div>
                        </div>
                      )}
                      {seg.translatedText && (
                        <div className="flex flex-col gap-1 border-t border-[#E2DCF0] pt-2 mt-1">
                          <p className="font-roboto font-bold text-[10px] text-[#3D2B90] uppercase">{t.translation}</p>
                          <div className="text-[13px] leading-relaxed text-[#3D2B90] font-roboto ai-markdown">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{seg.translatedText}</ReactMarkdown>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  {resultText && (
                    <div className="flex-1 min-h-0 flex flex-col">
                      {resultTranslation && (
                        <p className="font-roboto font-bold text-[10px] text-[#3D2B90] uppercase mb-1">{t.original}</p>
                      )}
                      <div className="w-full flex-1 min-h-0 overflow-y-auto bg-[#F3F1F9] rounded-[12px] p-3.5 text-[13px] leading-relaxed text-[#3D2B90] font-roboto ai-markdown scrobar-custom" style={{ scrollbarWidth: 'thin', scrollbarColor: '#3D2B90 #C7BFE4' }}>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{resultText}</ReactMarkdown>
                      </div>
                    </div>
                  )}
                  {resultTranslation && (
                    <div className="flex-1 min-h-0 flex flex-col">
                      <p className="font-roboto font-bold text-[10px] text-[#3D2B90] uppercase mb-1">{t.translation}</p>
                      <div className="w-full flex-1 min-h-0 overflow-y-auto bg-[#F3F1F9] rounded-[12px] p-3.5 text-[13px] leading-relaxed text-[#3D2B90] font-roboto ai-markdown scrobar-custom" style={{ scrollbarWidth: 'thin', scrollbarColor: '#3D2B90 #C7BFE4' }}>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{resultTranslation}</ReactMarkdown>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="flex flex-col gap-2 pt-2 border-t border-[#E2DCF0]">
              <button
                type="button"
                onClick={handleDownloadResult}
                className="flex items-center justify-center h-[36px] rounded-full bg-deep-purple text-white font-roboto font-semibold text-[11px] uppercase leading-none px-5 hover:bg-[#2e1f72] transition-colors"
              >
                {t.exportBtn}
              </button>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setShowResult(false)}
                  className="flex-1 flex items-center justify-center h-[36px] rounded-full border-2 border-[#3D2B90] text-[#3D2B90] font-roboto font-semibold text-[11px] uppercase leading-none px-5 hover:bg-[#F3F1F9] transition-colors"
                >
                  {t.cancelBtn}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (onResult) {
                      if (pendingResult) {
                        onResult(pendingResult);
                      } else if (resultTranslation) {
                        onResult({ originalText: resultText, translation: resultTranslation });
                      } else {
                        onResult(resultText, "original");
                      }
                    }
                    setPendingResult(null);
                    setShowResult(false);
                  }}
                  className="flex-1 flex items-center justify-center h-[36px] rounded-full bg-[#3D2B90] text-white font-roboto font-semibold text-[11px] uppercase leading-none px-5 hover:bg-[#2e1f72] transition-colors"
                >
                  {t.apply}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDownloadPicker && (
        <div
          className="fixed inset-0 z-[1002] flex items-center justify-center bg-black/50 px-4"
          onClick={() => setShowDownloadPicker(false)}
        >
          <div className="relative bg-white rounded-[27px] p-6 w-full max-w-sm flex flex-col gap-4" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setShowDownloadPicker(false)}
              className="absolute top-4 right-4 w-7 h-7 flex items-center justify-center text-[#3D2B90] hover:opacity-70 transition-opacity"
              aria-label="Close"
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 1L15 15M15 1L1 15" stroke="#3D2B90" strokeWidth="2.2" strokeLinecap="round" />
              </svg>
            </button>
            <h2 className="font-roboto font-black text-[15px] text-[#42445A] uppercase text-center">{t.downloadAs}</h2>
            <div className="flex gap-2.5 sm:gap-3">
              <button
                type="button"
                onClick={() => doDownload("text")}
                className="flex-1 flex items-center justify-center h-[40px] rounded-[67.52px] border-2 border-[#3D2B90] text-[#3D2B90] font-roboto font-semibold text-xs uppercase leading-none px-3 sm:px-4 hover:bg-[#F3F1F9] transition-colors"
              >
                {t.text}
              </button>
              <button
                type="button"
                onClick={() => doDownload("pdf")}
                className="flex-1 flex items-center justify-center h-[40px] rounded-[67.52px] border-2 border-[#3D2B90] text-[#3D2B90] font-roboto font-semibold text-xs uppercase leading-none px-3 sm:px-4 hover:bg-[#F3F1F9] transition-colors"
              >
                {t.pdf}
              </button>
              <button
                type="button"
                onClick={() => doDownload("word")}
                className="flex-1 flex items-center justify-center h-[40px] rounded-[67.52px] border-2 border-[#3D2B90] text-[#3D2B90] font-roboto font-semibold text-xs uppercase leading-none px-3 sm:px-4 hover:bg-[#F3F1F9] transition-colors"
              >
                {t.word || "WORD"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MenuAIPanel;
